import React from 'react';
import PropTypes from 'prop-types';
import { Table, TBody, TR, TD } from 'oy-vey';
import Layout from '../layouts/Layout';
import Header from '../modules/Header';
import EmptySpace from '../modules/EmptySpace';
import EmailSignature from './EmailSignature';

// An analysis failed or never happened, and operations need to know.
//
// Goes to operations, never to a host or a guest: none of them can do anything
// about an analysis service that is down, and telling them would only make the
// scan look unreliable.
//
// Written to be read at a glance and acted on. The failure text the service
// actually returned matters far more here than any category — the category only
// decides who reads it.
class AnalysisAlert extends React.Component {
  static propTypes = {
    content: PropTypes.shape({
      reservationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      problem: PropTypes.string,
      problemDescription: PropTypes.string,
      reportError: PropTypes.string,
      completedAt: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      listTitle: PropTypes.string,
      heldCount: PropTypes.number,
      reservationUrl: PropTypes.string,
      siteName: PropTypes.string,
      logo: PropTypes.string,
    }).isRequired
  };

  render() {
    const {
      content: {
        reservationId, problem, problemDescription, reportError,
        completedAt, listTitle, heldCount, reservationUrl, siteName, logo,
      }
    } = this.props;

    const textStyle = {
      color: '#333',
      backgroundColor: '#F7F7F7',
      fontFamily: 'Arial',
      fontSize: '16px',
      padding: '35px',
    };
    const linkStyle = { color: '#2d8f4b', fontWeight: 'bold', textDecoration: 'underline' };
    const errorStyle = {
      backgroundColor: '#FFF',
      border: '1px solid #E0E0E0',
      padding: '12px',
      fontFamily: 'monospace',
      fontSize: '13px',
      color: '#B00020',
    };
    const noteStyle = { fontSize: '14px', color: '#666' };

    return (
      <Layout>
        <Header color="#2d8f4b" backgroundColor="#F7F7F7" logo={logo} siteName={siteName} />
        <div>
          <Table width="100%">
            <TBody>
              <TR>
                <TD style={textStyle}>
                  <EmptySpace height={20} />
                  <div>
                    <strong>Reservation #{reservationId} has no damage scan report.</strong>
                  </div>
                  <EmptySpace height={20} />
                  <div>{problemDescription}</div>
                  <EmptySpace height={20} />
                  {reportError && (
                    <div>
                      <div style={noteStyle}>What the analysis service reported:</div>
                      <EmptySpace height={10} />
                      <div style={errorStyle}>{reportError}</div>
                      <EmptySpace height={20} />
                    </div>
                  )}
                  <div style={noteStyle}>
                    {listTitle ? `${listTitle}. ` : ''}
                    {completedAt ? `Trip completed ${completedAt}. ` : ''}
                    Problem: {problem}.
                  </div>
                  <EmptySpace height={20} />
                  {reservationUrl && (
                    <div>
                      <a href={reservationUrl} style={linkStyle}>Open the reservation</a>
                    </div>
                  )}
                  <EmptySpace height={20} />
                  {/* The completion hold is correct behaviour, so it is context
                      rather than its own alert — but a backlog of held trips is
                      worth seeing next to a failure, since both point at the
                      same part of the system. */}
                  {heldCount > 0 && (
                    <div style={noteStyle}>
                      {heldCount} {heldCount === 1 ? 'trip is' : 'trips are'} waiting in the scan hold.
                    </div>
                  )}
                  <EmptySpace height={20} />
                  {/* Said explicitly, because the obvious assumption on
                      receiving one of these is that more will follow. */}
                  <div style={noteStyle}>
                    This is sent once per reservation per problem. It will not repeat unless the
                    problem changes.
                  </div>
                  <EmptySpace height={20} />
                  <EmailSignature siteName={siteName} />
                </TD>
              </TR>
            </TBody>
          </Table>
        </div>
      </Layout>
    );
  }
}

export default AnalysisAlert;
